import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useRefData } from "@/context/RefDataContext";
import { PageHeader, fmtDate, StatusBadge } from "@/components/shared";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Plus, StickyNote, Trash2, ArrowRight, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const TYPES = [
  { value: "progress", label: "Progress" },
  { value: "behaviour", label: "Behaviour" },
  { value: "warning", label: "Warning" },
  { value: "achievement", label: "Achievement" },
];
const TYPE_BADGE = { progress: "scheduled", behaviour: "pending", warning: "suspended", achievement: "active" };

export default function ProgressNotes() {
  const { user } = useAuth();
  const ref = useRefData();
  const canManage = user?.role !== "client";
  const [notes, setNotes] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ dog_id: "", note_type: "progress", note: "", recommended_next_step: "", client_visible: true });

  const load = async () => { const r = await api.get("/progress-notes"); setNotes(r.data); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try { await api.post("/progress-notes", form); toast.success("Note added"); setOpen(false); setForm({ dog_id: "", note_type: "progress", note: "", recommended_next_step: "", client_visible: true }); await load(); }
    catch (e) { toast.error("Failed"); }
  };
  const remove = async (id) => { await api.delete(`/progress-notes/${id}`); toast.success("Deleted"); await load(); };

  return (
    <div>
      <PageHeader
        title="Progress Notes"
        subtitle="Track training progress, behaviour and achievements for each dog."
        action={canManage && <Button className="gap-2" onClick={() => setOpen(true)} data-testid="pn-add-btn"><Plus size={16} strokeWidth={1.5} /> New Note</Button>}
      />
      <div className="space-y-3">
        {notes.map((n) => (
          <Card key={n.id} className="border-border" data-testid="progress-note-item">
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <StickyNote size={16} className="text-primary" strokeWidth={1.5} />
                    <span className="font-medium">{n.dog_name}</span>
                    <StatusBadge value={TYPE_BADGE[n.note_type]} />
                    <span className="text-xs text-muted-foreground capitalize">{n.note_type}</span>
                  </div>
                  <p className="text-sm mt-2">{n.note}</p>
                  {n.recommended_next_step && (
                    <div className="flex items-center gap-1.5 text-sm text-primary mt-1.5"><ArrowRight size={14} /> {n.recommended_next_step}</div>
                  )}
                  <div className="text-xs text-muted-foreground mt-2 flex items-center gap-2">
                    <span>{n.trainer_name} · {fmtDate(n.date)}</span>
                    {n.client_visible === false
                      ? <span className="flex items-center gap-1 text-muted-foreground/70"><EyeOff size={11} /> Internal</span>
                      : <span className="flex items-center gap-1 text-green-700"><Eye size={11} /> Visible to owner</span>}
                  </div>
                </div>
                {canManage && <Button variant="ghost" size="icon" onClick={() => remove(n.id)} data-testid="pn-delete"><Trash2 size={15} className="text-destructive" /></Button>}
              </div>
            </CardContent>
          </Card>
        ))}
        {notes.length === 0 && <p className="text-muted-foreground text-center py-12">No progress notes yet.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">New Progress Note</DialogTitle>
            <DialogDescription>Record training progress, behaviour or an achievement for a dog.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-1.5"><Label>Dog</Label>
              <Select value={form.dog_id} onValueChange={(v) => setForm({ ...form, dog_id: v })}>
                <SelectTrigger data-testid="pn-dog"><SelectValue placeholder="Select dog" /></SelectTrigger>
                <SelectContent>{ref.dogs.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5"><Label>Note type</Label>
              <Select value={form.note_type} onValueChange={(v) => setForm({ ...form, note_type: v })}>
                <SelectTrigger data-testid="pn-type"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5"><Label>Note</Label><Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="pn-note" /></div>
            <div className="grid gap-1.5"><Label>Recommended next step</Label><Input value={form.recommended_next_step} onChange={(e) => setForm({ ...form, recommended_next_step: e.target.value })} data-testid="pn-next" /></div>
            <div className="flex items-center justify-between rounded-lg border border-border/60 p-3">
              <div>
                <Label className="flex items-center gap-1.5">{form.client_visible ? <Eye size={14} /> : <EyeOff size={14} />} Visible to owner</Label>
                <p className="text-xs text-muted-foreground mt-0.5">Owners can see this note in their portal.</p>
              </div>
              <Switch checked={form.client_visible} onCheckedChange={(v) => setForm({ ...form, client_visible: v })} data-testid="pn-client-visible" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={!form.dog_id || !form.note} data-testid="pn-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
